import apiClient, { ApiResponse, PaginatedResponse } from '../api/client';

// ============================================================================
// MODULE SERVICES
// ============================================================================
export interface ModuleServiceItem {
  id: number;
  module_id: number;
  category_id?: number;
  name: string;
  description?: string;
  billing_type?: string;
  billing_cycle_id?: number;
  amount?: number;
  icon?: string;
  has_printable?: boolean;
  printable_type?: string;
  printable_template?: string;
  printable_config?: any;
  is_billable?: boolean;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
  category?: any;
  billing_cycle?: any;
}

export interface CreateModuleServiceData {
  name: string;
  description?: string;
  category?: number;
  category_id?: number;
  billing_type?: string;
  billing_cycle?: number | null;
  billing_cycle_id?: number;
  amount?: number;
  icon?: string;
  has_printable?: boolean;
  printable_type?: string;
  printable_template?: string;
  printable_config?: any;
  is_billable?: boolean;
  is_active?: boolean;
}

class ModuleServiceService {
  private getBaseUrl(moduleId: number) {
    return `/admin/county/modules/${moduleId}/services`;
  }

  async getAll(moduleId: number): Promise<ApiResponse<ModuleServiceItem[]>> {
    return apiClient.get<ModuleServiceItem[]>(this.getBaseUrl(moduleId));
  }

  async getOne(moduleId: number, serviceId: number): Promise<ApiResponse<ModuleServiceItem>> {
    return apiClient.get<ModuleServiceItem>(`${this.getBaseUrl(moduleId)}/${serviceId}`);
  }

  async create(moduleId: number, data: CreateModuleServiceData): Promise<ApiResponse<ModuleServiceItem>> {
    return apiClient.post<ModuleServiceItem>(this.getBaseUrl(moduleId), data);
  }

  async update(moduleId: number, serviceId: number, data: Partial<CreateModuleServiceData>): Promise<ApiResponse<ModuleServiceItem>> {
    return apiClient.put<ModuleServiceItem>(`${this.getBaseUrl(moduleId)}/${serviceId}`, data);
  }

  async delete(moduleId: number, serviceId: number): Promise<ApiResponse> {
    return apiClient.delete(`${this.getBaseUrl(moduleId)}/${serviceId}`);
  }
}

// ============================================================================
// MODULE SCHEDULES
// ============================================================================
export interface ModuleSchedule {
  id: number;
  module_id: number;
  name: string;
  description?: string;
  start_date?: string;
  end_date?: string;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface CreateModuleScheduleData {
  name: string;
  description?: string;
  start_date?: string;
  end_date?: string;
  is_active?: boolean;
}

class ModuleScheduleService {
  private getBaseUrl(moduleId: number) {
    return `/admin/county/modules/${moduleId}/schedules`;
  }

  // Get all schedules for the county (across all modules)
  async getAllForCounty(): Promise<ApiResponse<ModuleSchedule[]>> {
    return apiClient.get<ModuleSchedule[]>('/admin/county/schedules');
  }

  // Get schedules for a specific module
  async getAll(moduleId: number): Promise<ApiResponse<ModuleSchedule[]>> {
    return apiClient.get<ModuleSchedule[]>(this.getBaseUrl(moduleId));
  }

  async getOne(scheduleId: number): Promise<ApiResponse<ModuleSchedule>> {
    return apiClient.get<ModuleSchedule>(`/admin/county/schedules/${scheduleId}`);
  }

  // Create county-wide schedule (not module-specific)
  async create(data: CreateModuleScheduleData): Promise<ApiResponse<ModuleSchedule>> {
    return apiClient.post<ModuleSchedule>('/admin/county/schedules', data);
  }

  // Update county-wide schedule
  async update(scheduleId: number, data: Partial<CreateModuleScheduleData>): Promise<ApiResponse<ModuleSchedule>> {
    return apiClient.put<ModuleSchedule>(`/admin/county/schedules/${scheduleId}`, data);
  }

  // Delete county-wide schedule
  async delete(scheduleId: number): Promise<ApiResponse> {
    return apiClient.delete(`/admin/county/schedules/${scheduleId}`);
  }
}

// ============================================================================
// MODULE PARTS
// ============================================================================
export interface ModulePart {
  id: number;
  module_id: number;
  name: string;
  description?: string;
  quantity?: number;
  unit?: string;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface CreateModulePartData {
  name: string;
  description?: string;
  quantity?: number;
  unit?: string;
  is_active?: boolean;
}

class ModulePartService {
  private getBaseUrl(moduleId: number) {
    return `/admin/county/modules/${moduleId}/parts`;
  }

  // Get all parts for the county (across all modules)
  async getAllForCounty(): Promise<ApiResponse<ModulePart[]>> {
    return apiClient.get<ModulePart[]>('/admin/county/parts');
  }

  // Get parts for a specific module
  async getAll(moduleId: number): Promise<ApiResponse<ModulePart[]>> {
    return apiClient.get<ModulePart[]>(this.getBaseUrl(moduleId));
  }

  async getOne(partId: number): Promise<ApiResponse<ModulePart>> {
    return apiClient.get<ModulePart>(`/admin/county/parts/${partId}`);
  }

  // Create county-wide part (not module-specific)
  async create(data: CreateModulePartData): Promise<ApiResponse<ModulePart>> {
    return apiClient.post<ModulePart>('/admin/county/parts', data);
  }

  // Update county-wide part
  async update(partId: number, data: Partial<CreateModulePartData>): Promise<ApiResponse<ModulePart>> {
    return apiClient.put<ModulePart>(`/admin/county/parts/${partId}`, data);
  }

  // Delete county-wide part
  async delete(partId: number): Promise<ApiResponse> {
    return apiClient.delete(`/admin/county/parts/${partId}`);
  }
}

// ============================================================================
// MODULE CATEGORIES
// ============================================================================
export interface ModuleCategory {
  id: number;
  module_id: number;
  name: string;
  description?: string;
  parent_id?: number;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
  children?: ModuleCategory[];
  parent?: ModuleCategory;
}

export interface CreateModuleCategoryData {
  name: string;
  description?: string;
  parent_id?: number;
  is_active?: boolean;
}

class ModuleCategoryService {
  private getBaseUrl(moduleId: number) {
    return `/admin/county/modules/${moduleId}/categories`;
  }

  async getAll(moduleId: number): Promise<ApiResponse<ModuleCategory[]>> {
    return apiClient.get<ModuleCategory[]>(this.getBaseUrl(moduleId));
  }

  async getOne(moduleId: number, categoryId: number): Promise<ApiResponse<ModuleCategory>> {
    return apiClient.get<ModuleCategory>(`${this.getBaseUrl(moduleId)}/${categoryId}`);
  }

  async create(moduleId: number, data: CreateModuleCategoryData): Promise<ApiResponse<ModuleCategory>> {
    return apiClient.post<ModuleCategory>(this.getBaseUrl(moduleId), data);
  }

  async update(moduleId: number, categoryId: number, data: Partial<CreateModuleCategoryData>): Promise<ApiResponse<ModuleCategory>> {
    return apiClient.put<ModuleCategory>(`${this.getBaseUrl(moduleId)}/${categoryId}`, data);
  }

  async delete(moduleId: number, categoryId: number): Promise<ApiResponse> {
    return apiClient.delete(`${this.getBaseUrl(moduleId)}/${categoryId}`);
  }
}

// ============================================================================
// MODULE CHARGES
// ============================================================================
export interface ModuleCharge {
  id: number;
  module_id: number;
  service_id?: number;
  zone_id?: number;
  amount: number;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
  service?: any;
  zone?: any;
}

export interface CreateModuleChargeData {
  service_id?: number;
  zone_id?: number;
  amount: number;
  is_active?: boolean;
}

class ModuleChargeService {
  private getBaseUrl(moduleId: number) {
    return `/admin/county/modules/${moduleId}/charges`;
  }

  async getAll(moduleId: number): Promise<ApiResponse<ModuleCharge[]>> {
    return apiClient.get<ModuleCharge[]>(this.getBaseUrl(moduleId));
  }

  async getOne(moduleId: number, chargeId: number): Promise<ApiResponse<ModuleCharge>> {
    return apiClient.get<ModuleCharge>(`${this.getBaseUrl(moduleId)}/${chargeId}`);
  }

  async create(moduleId: number, data: CreateModuleChargeData): Promise<ApiResponse<ModuleCharge>> {
    return apiClient.post<ModuleCharge>(this.getBaseUrl(moduleId), data);
  }

  async update(moduleId: number, chargeId: number, data: Partial<CreateModuleChargeData>): Promise<ApiResponse<ModuleCharge>> {
    return apiClient.put<ModuleCharge>(`${this.getBaseUrl(moduleId)}/${chargeId}`, data);
  }

  async delete(moduleId: number, chargeId: number): Promise<ApiResponse> {
    return apiClient.delete(`${this.getBaseUrl(moduleId)}/${chargeId}`);
  }
}

// ============================================================================
// MODULE PENALTIES
// ============================================================================
export interface ModulePenalty {
  id: number;
  module_id: number;
  service_id?: number;
  billing_cycle_id?: number;
  type?: number;
  frequency?: number;
  amount: number;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
  service?: any;
  billing_cycle?: any;
}

export interface CreateModulePenaltyData {
  service_id?: number;
  billing_cycle_id?: number;
  type?: number;
  frequency?: number;
  amount: number;
  is_active?: boolean;
}

class ModulePenaltyService {
  private getBaseUrl(moduleId: number) {
    return `/admin/county/modules/${moduleId}/penalties`;
  }

  async getAll(moduleId: number): Promise<ApiResponse<ModulePenalty[]>> {
    return apiClient.get<ModulePenalty[]>(this.getBaseUrl(moduleId));
  }

  async getOne(moduleId: number, penaltyId: number): Promise<ApiResponse<ModulePenalty>> {
    return apiClient.get<ModulePenalty>(`${this.getBaseUrl(moduleId)}/${penaltyId}`);
  }

  async create(moduleId: number, data: CreateModulePenaltyData): Promise<ApiResponse<ModulePenalty>> {
    return apiClient.post<ModulePenalty>(this.getBaseUrl(moduleId), data);
  }

  async update(moduleId: number, penaltyId: number, data: Partial<CreateModulePenaltyData>): Promise<ApiResponse<ModulePenalty>> {
    return apiClient.put<ModulePenalty>(`${this.getBaseUrl(moduleId)}/${penaltyId}`, data);
  }

  async delete(moduleId: number, penaltyId: number): Promise<ApiResponse> {
    return apiClient.delete(`${this.getBaseUrl(moduleId)}/${penaltyId}`);
  }
}

// ============================================================================
// MODULE DISCOUNTS
// ============================================================================
export interface ModuleDiscount {
  id: number;
  module_id: number;
  service_id: number;
  name: string;
  description?: string;
  type: number; // 1: Fixed Amount, 2: Percentage
  value: number;
  start_date?: string;
  end_date?: string;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
  service?: any;
  module?: any;
}

export interface CreateModuleDiscountData {
  service_id: number;
  name: string;
  description?: string;
  type: number; // 1: Fixed Amount, 2: Percentage
  value: number;
  start_date?: string;
  end_date?: string;
  is_active?: boolean;
}

class ModuleDiscountService {
  private getBaseUrl(moduleId: number) {
    return `/admin/county/modules/${moduleId}/discounts`;
  }

  async getAll(moduleId: number): Promise<ApiResponse<ModuleDiscount[]>> {
    return apiClient.get<ModuleDiscount[]>(this.getBaseUrl(moduleId));
  }

  async getOne(moduleId: number, discountId: number): Promise<ApiResponse<ModuleDiscount>> {
    return apiClient.get<ModuleDiscount>(`${this.getBaseUrl(moduleId)}/${discountId}`);
  }

  async create(moduleId: number, data: CreateModuleDiscountData): Promise<ApiResponse<ModuleDiscount>> {
    return apiClient.post<ModuleDiscount>(this.getBaseUrl(moduleId), data);
  }

  async update(moduleId: number, discountId: number, data: Partial<CreateModuleDiscountData>): Promise<ApiResponse<ModuleDiscount>> {
    return apiClient.put<ModuleDiscount>(`${this.getBaseUrl(moduleId)}/${discountId}`, data);
  }

  async delete(moduleId: number, discountId: number): Promise<ApiResponse> {
    return apiClient.delete(`${this.getBaseUrl(moduleId)}/${discountId}`);
  }

  async updateStatus(moduleId: number, discountId: number, isActive: boolean): Promise<ApiResponse<ModuleDiscount>> {
    return apiClient.patch<ModuleDiscount>(`${this.getBaseUrl(moduleId)}/${discountId}/status`, { is_active: isActive });
  }
}

// ============================================================================
// EXPORTS
// ============================================================================
export const moduleServiceService = new ModuleServiceService();
export const moduleScheduleService = new ModuleScheduleService();
export const modulePartService = new ModulePartService();
export const moduleCategoryService = new ModuleCategoryService();
export const moduleChargeService = new ModuleChargeService();
export const modulePenaltyService = new ModulePenaltyService();
export const moduleDiscountService = new ModuleDiscountService();
